import React, { useEffect, useState } from 'react';
import {
  HardDrive, ShieldCheck, KeyRound, Database, UploadCloud, Server, Eye, ChevronRight, ChevronDown,
  Lock, Download, ListTree
} from 'lucide-react';
import SiteFooter from './SiteFooter.jsx';

const SECTIONS = [
  { id: 'einfuehrung', label: 'Einführung' },
  { id: 'prinzip', label: 'Zero-Knowledge' },
  { id: 'einrichtung', label: 'Ersteinrichtung' },
  { id: 'schluessel', label: 'Schlüsselerzeugung' },
  { id: 'passwort-recovery', label: 'Passwort & Recovery' },
  { id: 'session', label: 'Entsperren & Sperren' },
  { id: 'ram-sicherheit', label: 'Schlüssel im RAM' },
  { id: 'threat-model', label: 'Schutz und Grenzen' },
  { id: 'dashboard-einbettung', label: 'Im Dashboard' },
  { id: 'abo-ende', label: 'Nach dem Abo-Ende' },
  { id: 'sicherheits-einstellungen', label: 'Sicherheits-Einstellungen' },
  { id: 'dateiverschluesselung', label: 'Verschlüsselung' },
  { id: 'download', label: 'Download' },
  { id: 'lokaler-index', label: 'Dateiindex & Sync' },
  { id: 'upload', label: 'Upload' },
  { id: 'loeschnachweis', label: 'Löschnachweis' },
  { id: 'metadaten', label: 'Metadaten' },
  { id: 'krypto-parameter', label: 'Parameter' },
  { id: 'kdf-wahl', label: 'Warum PBKDF2?' },
  { id: 'server', label: 'Server (Überblick)' },
  { id: 'pruefen', label: 'Selbst prüfen' },
  { id: 'grenzen', label: 'Grenzen' },
];

function CodeBlock({ title, children }) {
  return (
    <figure className="docs-code-block">
      {title && <figcaption className="docs-code-caption">{title}</figcaption>}
      <pre className="docs-code"><code>{children}</code></pre>
    </figure>
  );
}

function Callout({ type = 'info', title, children }) {
  return (
    <aside className={`docs-callout docs-callout-${type}`}>
      {title && <div className="docs-callout-title">{title}</div>}
      <div>{children}</div>
    </aside>
  );
}

function ParamTable({ rows }) {
  return (
    <div className="docs-table-wrap">
      <table className="ed-table docs-table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Wert</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}><td>{k}</td><td>{v}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const MOBILE_TOC_MQ = '(max-width: 900px)';

export default function DocsPage() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [tocOpen, setTocOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !window.matchMedia(MOBILE_TOC_MQ).matches;
  });

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && SECTIONS.some((s) => s.id === hash)) setActiveId(hash);

    const onHash = () => {
      const h = window.location.hash.replace('#', '');
      if (h && SECTIONS.some((s) => s.id === h)) setActiveId(h);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: [0, 0.2, 0.5] }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const navTo = (id) => {
    setActiveId(id);
    window.location.hash = id;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (window.matchMedia(MOBILE_TOC_MQ).matches) setTocOpen(false);
  };

  return (
    <div className="docs-page ed-page min-h-screen">
      <header className="docs-header">
        <div className="docs-header-inner">
          <a href="/" className="docs-brand">
            <HardDrive className="w-6 h-6" />
            <span>
              <span className="docs-brand-title">Tresor</span>
              <span className="ed-eyebrow">Technische Dokumentation</span>
            </span>
          </a>
          <nav className="docs-header-nav">
            <a href="/" className="ed-link text-sm">Zur Startseite</a>
          </nav>
        </div>
      </header>

      <div className="docs-layout">
        <aside className="docs-sidebar">
          <button
            type="button"
            className="docs-toc-toggle"
            aria-expanded={tocOpen}
            aria-controls="docs-toc-nav"
            onClick={() => setTocOpen((open) => !open)}
          >
            <span>Inhaltsverzeichnis</span>
            <ChevronDown className="docs-toc-toggle-icon" aria-hidden="true" />
          </button>
          <p className="ed-eyebrow mb-4">Inhalt</p>
          <nav
            id="docs-toc-nav"
            aria-label="Dokumentationsnavigation"
            className={`docs-toc-nav ${tocOpen ? 'is-open' : ''}`}
          >
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => navTo(s.id)}
                className={`docs-nav-item ${activeId === s.id ? 'is-active' : ''}`}
              >
                <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-50" />
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="docs-main">
          <section id="einfuehrung" className="docs-section">
            <h1 className="docs-h1">Wie Ihr Tresor verschlüsselt</h1>
            <p className="docs-lead">
              Diese Seite beschreibt, <strong>was in Ihrem Browser passiert</strong>, bevor Dateien
              Ihren Rechner verlassen. Der Code, der das tut, wird mitsamt Sourcemaps ausgeliefert:
              in den Entwicklerwerkzeugen unter „Sources" steht <code>crypto.js</code> Zeile für Zeile
              so da, wie er im Repository liegt. Wie Sie das prüfen, steht unter{' '}
              <a href="/nachpruefen/" className="ed-link">Quelltext nachprüfen</a>.
            </p>
            <ul className="docs-list">
              <li><strong>Hier dokumentiert:</strong> Schlüsselableitung, Dateiverschlüsselung, Dateiindex & Sync, öffentliche API-Schnittstellen</li>
              <li><strong>Nur Überblick:</strong> interne Server-Implementierung und Betriebsdetails</li>
              <li><strong>Nie über das Netz:</strong> Ihr Tresor-Passwort, Ihre Recovery-Phrase, der unverschlüsselte Master-Schlüssel, unverschlüsselte Dateiinhalte</li>
              <li><strong>Auf dem Server:</strong> Salts, verschlüsselte Hüllen (<code>passwordWrapped</code>, <code>recoveryWrapped</code>) und ein <strong>öffentlicher</strong> Ed25519-Schlüssel, mit dem sich ein Passwort-Reset nur <em>prüfen</em>, nicht <em>unterschreiben</em> lässt</li>
            </ul>
          </section>

          <section id="prinzip" className="docs-section">
            <h2 className="docs-h2"><ShieldCheck className="docs-h2-icon" /> Das Prinzip</h2>
            <p>
              Der Server speichert nur <em>verschlüsselte</em> Dateiinhalte und technische Hüllen (Salts, Wrapped Keys).
              Ihr Master-Schlüssel entsteht beim ersten Setup in Ihrem Browser und wird dort in Hüllen verpackt,
              bevor etwas an den Server geht. Beim Entsperren wird er ausschließlich lokal wieder freigegeben.
              Ohne Ihr Passwort oder Ihre Recovery-Phrase können Inhalte nicht gelesen werden, auch nicht durch den Betreiber.
            </p>
            <div className="docs-flow">
              <div className="docs-flow-step"><span>1</span> Sie geben Passwort oder Recovery ein (nur lokal)</div>
              <div className="docs-flow-arrow">→</div>
              <div className="docs-flow-step"><span>2</span> Browser entschlüsselt die Hülle → Master-Key im RAM</div>
              <div className="docs-flow-arrow">→</div>
              <div className="docs-flow-step"><span>3</span> AES-GCM lokal für Dateien</div>
              <div className="docs-flow-arrow">→</div>
              <div className="docs-flow-step"><span>4</span> Nur Ciphertext geht raus</div>
            </div>
            <Callout type="warn" title="Was Zero-Knowledge nicht bedeutet">
              Bestimmte Betriebsdaten sind für uns sichtbar: Dateigrößen, Anzahl der Datenblöcke,
              Zeitstempel und Ihre Speicherbelegung. Dateinamen und Ordnerstruktur gehören nicht dazu,
              die liegen verschlüsselt im Änderungsjournal. Inhalte und Schlüssel bleiben geschützt. Details finden Sie im Abschnitt <a href="#metadaten" className="ed-link" onClick={(e) => { e.preventDefault(); navTo('metadaten'); }}>Metadaten</a>.
            </Callout>
          </section>

          <section id="einrichtung" className="docs-section">
            <h2 className="docs-h2"><ListTree className="docs-h2-icon" /> Ersteinrichtung Ihres Tresors</h2>
            <p>Beim ersten Öffnen legen Sie Passwort und Recovery-Phrase fest. Intern passiert Folgendes:</p>
            <ol className="docs-ol">
              <li>Sie wählen ein Passwort (mindestens 12 Zeichen).</li>
              <li>Der Browser erzeugt zufällige Salts und würfelt 32 Master-Bytes per CSPRNG (<code>generateMasterBits()</code>).</li>
              <li>Diese Bytes werden unter Ihrer <strong>Recovery-Phrase</strong> versiegelt (Wrap).</li>
              <li>Dieselben Bytes werden unter Ihrem <strong>Passwort</strong> separat versiegelt.</li>
              <li>Aus denselben Bytes leitet der Browser per HKDF zwei weitere Schlüssel ab: einen für den lokalen Dateiindex und ein Ed25519-Paar für den <a href="#loeschnachweis" className="ed-link" onClick={(e) => { e.preventDefault(); navTo('loeschnachweis'); }}>Löschnachweis</a>.</li>
              <li>Nur die Hüllen, die Salts und zwei öffentliche Schlüssel werden an den Server gesendet: nie das Passwort, nie die Recovery-Wörter, nie der Master-Schlüssel im Klartext.</li>
            </ol>
            <p className="docs-muted">
              <code>passwordWrapped</code> ist dabei <strong>nicht</strong> Ihr Passwort in verschlüsselter Form,
              sondern der <strong>Master-Schlüssel</strong> (32 Byte), mit einem vom Passwort abgeleiteten Schlüssel versiegelt.
            </p>
            <CodeBlock title="src/crypto.js · Master-Key unter Recovery versiegeln">
{`export async function wrapMasterBits(masterBits, kek) {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const ct = await globalThis.crypto.subtle.encrypt(
    { name: "AES-GCM", iv }, kek, masterBits
  );
  return { wrappedHex: toHex(new Uint8Array(ct)), ivHex: toHex(iv) };
}

// Das ist alles, was beim Setup zum Server geht. Vollstaendig, nicht "unter anderem":
// salt, recoverySalt, recoveryWrapped, recoveryIv,
// recoveryProofKey (oeffentlich), recoveryProofAlg,
// passwordWrapped, passwordIv,
// deleteProofKey (oeffentlich)`}
            </CodeBlock>
            <Callout type="note" title="Herkunft der Code-Auszüge">
              Die Auszüge stammen aus dem ausgelieferten Frontend (<code>crypto.js</code>,{' '}
              <code>db.js</code>, <code>uploads.js</code>), Stand Oktober 2026. Sie sind gekürzt,
              aber nicht umgeschrieben. Maßgeblich ist der Code, den Ihr Browser lädt, und den
              sehen Sie über <a href="/nachpruefen/" className="ed-link">Quelltext nachprüfen</a>.
            </Callout>
            <Callout type="info" title="Warum zwei Hüllen?">
              Das Passwort nutzen Sie täglich zum Entsperren. Die Recovery-Phrase ist Ihr Notfall-Schlüssel,
              falls Sie das Passwort vergessen. Beide Kanäle haben eigene Salts, damit sie kryptografisch
              voneinander getrennt bleiben.
            </Callout>
          </section>

          <section id="schluessel" className="docs-section">
            <h2 className="docs-h2"><KeyRound className="docs-h2-icon" /> Schlüsselerzeugung</h2>
            <p>
              Beim <strong>ersten Setup</strong> würfelt der Browser 32 zufällige Master-Bytes (CSPRNG). Aus Ihrem
              Tresor-Passwort wird per <strong>PBKDF2-SHA256</strong> mit <strong>600.000 Iterationen</strong> nur der
              Schlüssel abgeleitet, der diese Bytes in <code>passwordWrapped</code> versiegelt. Beim <strong>Entsperren</strong>
              leitet der Browser aus Ihrem Passwort einen KEK ab, um die Hülle lokal zu öffnen.
              Der Session-Schlüssel ist <code>extractable: false</code> und verlässt den Arbeitsspeicher nicht in lesbarer Form.
            </p>
            <CodeBlock title="src/crypto.js · Master-Key erzeugen & KEK ableiten">
{`export function generateMasterBits() {
  return globalThis.crypto.getRandomValues(new Uint8Array(32));
}

const PBKDF2_ITERATIONS = 600000;

export async function deriveKekFromMnemonic(mnemonic, recoverySalt) {
  const enc = new TextEncoder();
  const keyMaterial = await globalThis.crypto.subtle.importKey(
    "raw", enc.encode(mnemonic.trim()), { name: "PBKDF2" }, false, ["deriveKey"]
  );
  return await globalThis.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: enc.encode(recoverySalt),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,  // extractable: false, Schlüssel bleibt im RAM
    ["encrypt", "decrypt"]
  );
}

// Der Passwort-Kanal nutzt denselben Umschlag, nur mit dem Passwort-Salt.
export async function deriveKekFromPassword(password, salt) {
  return deriveKekFromMnemonic(password, salt);
}`}
            </CodeBlock>
          </section>

          <section id="passwort-recovery" className="docs-section">
            <h2 className="docs-h2">Passwort-Verifikation & Recovery-Phrase</h2>
            <p>
              <strong>Ihr Tresor-Passwort wird weder gespeichert noch an den Server übertragen.</strong> Der Server
              erhält und behält nur eine verschlüsselte Hülle: den Master-Schlüssel, verpackt mit AES-GCM unter einem
              Schlüssel, der aus Ihrem Passwort und einem Salt abgeleitet wird. Erst wenn Sie im Browser Ihr Passwort
              eingeben, kann diese Hülle geöffnet und der Master-Schlüssel im RAM genutzt werden.
            </p>

            <div className="docs-table-wrap">
              <table className="ed-table docs-table">
                <thead>
                  <tr>
                    <th>Daten</th>
                    <th>Geht an den Server?</th>
                    <th>Liegt auf dem Server?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Tresor-Passwort (Klartext)</td><td>Nein</td><td>Nein</td></tr>
                  <tr><td>Recovery-Phrase (Klartext)</td><td>Nein</td><td>Nein</td></tr>
                  <tr><td>Master-Schlüssel (Klartext)</td><td>Nein</td><td>Nein</td></tr>
                  <tr><td><code>salt</code> (Passwort-Kanal)</td><td>Ja (beim Setup)</td><td>Ja (öffentlich, kein Geheimnis)</td></tr>
                  <tr><td><code>passwordWrapped</code> + <code>passwordIv</code></td><td>Ja (beim Setup)</td><td>Ja (verschlüsselter Master-Key)</td></tr>
                  <tr><td><code>recoveryWrapped</code> + <code>recoveryIv</code> + <code>recoverySalt</code></td><td>Ja (beim Setup)</td><td>Ja (verschlüsselter Master-Key, Recovery-Kanal)</td></tr>
                  <tr><td><code>recoveryProofKey</code> (öffentlicher Ed25519-Schlüssel)</td><td>Ja (beim Setup)</td><td>Ja (prüft Passwort-Reset-Anfragen, kann selbst nichts unterschreiben und nichts entschlüsseln)</td></tr>
                  <tr><td><code>deleteProofKey</code> (öffentlicher Ed25519-Schlüssel)</td><td>Ja (beim Setup)</td><td>Ja (prüft Löschanfragen, kann selbst nichts unterschreiben und nichts entschlüsseln)</td></tr>
                  <tr><td>Schlüssel je Datei (Klartext)</td><td>Nein</td><td>Nein (nur mit dem Master-Schlüssel verschlüsselt, im Journal)</td></tr>
                </tbody>
              </table>
            </div>

            <h3 className="docs-h3">Ersteinrichtung (Setup)</h3>
            <ol className="docs-ol">
              <li>Sie geben Ihr Passwort ein (bleibt im Browser).</li>
              <li>Der Browser erzeugt <code>salt</code>, würfelt 32 Master-Bytes (CSPRNG) und leitet den KEK aus dem Passwort ab (PBKDF2).</li>
              <li>Diese Bytes werden mit einem passwort-abgeleiteten Schlüssel versiegelt → <code>passwordWrapped</code>.</li>
              <li>Parallel dasselbe für die Recovery-Phrase → <code>recoveryWrapped</code>.</li>
              <li>Salts, Hüllen und der öffentliche Ed25519-Schlüssel gehen an <code>POST /api/tresor/setup</code>. Der zugehörige private Schlüssel wird bei Bedarf neu aus Ihren 12 Wörtern abgeleitet und nirgends gespeichert.</li>
            </ol>

            <h3 className="docs-h3">Entsperren (Unlock)</h3>
            <ol className="docs-ol">
              <li>Der Browser lädt <code>salt</code>, <code>passwordWrapped</code> und <code>passwordIv</code> vom Server.</li>
              <li>Sie geben Ihr Passwort ein (nur lokal, nie im Request).</li>
              <li>Daraus wird ein Schlüssel abgeleitet, die Hülle entschlüsselt, der Master-Key importiert.</li>
              <li>Erst jetzt kann der Tresor Dateien entschlüsseln. Falsches Passwort → AES-GCM schlägt fehl, kein Fallback.</li>
            </ol>

            <CodeBlock title="src/crypto.js · Entsperren per Passwort (vollständig lokal)">
{`export async function unlockWithPassword(password, salt, passwordWrapped, passwordIv) {
  if (!passwordWrapped || !passwordIv) {
    throw new Error('PASSWORD_ENVELOPE_MISSING');
  }
  // password bleibt im Browser; Server sieht es nie
  const kek = await deriveKekFromPassword(password, salt);
  const masterBits = await unwrapMasterBits(passwordWrapped, passwordIv, kek);
  return importMasterKeyFromBits(masterBits);  // sessionKey, nur RAM
}`}
            </CodeBlock>

            <p>
              Ihre <strong>12-Wörter Recovery-Phrase</strong> folgt BIP39 (128 Bit Entropie + Prüfsumme) und
              funktioniert über einen separaten Kanal (<code>recoverySalt</code>, <code>recoveryWrapped</code>).
              Auch hier gilt: die Wörter verlassen Ihren Browser nicht. Auf dem Server liegen nur die
              verschlüsselte Hülle und ein daraus abgeleiteter <strong>öffentlicher</strong>
              Ed25519-Schlüssel, mit dem er Reset-Anfragen prüfen kann.
            </p>
            <CodeBlock title="src/crypto.js · Recovery-Mnemonic erzeugen (Auszug)">
{`export async function generateMnemonic() {
  const entropy = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const hash = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', entropy));
  // 128 Bit Entropie + 4 Bit Pruefsumme aus dem ersten Hash-Byte -> 132 Bit,
  // in Bloecken zu 11 Bit auf die 2048 Woerter der BIP39-Liste abgebildet.
  return words.join(' ');
}`}
            </CodeBlock>

            <h3 className="docs-h3">Stellt die Recovery-Phrase mein Passwort wieder her?</h3>
            <p>
              <strong>Nein.</strong> Ihr Tresor-Passwort wird nirgends gespeichert, weder im Klartext noch
              verschlüsselt. Die Recovery-Phrase ist kein „Passwort-Knacker“, sondern ein <strong>zweiter Schlüssel
              zum gleichen Master-Schlüssel</strong>. Beim Setup werden dieselben 32 Master-Bytes einmal unter Ihrem
              Passwort und einmal unter Ihrer Phrase verpackt (<code>passwordWrapped</code> und{' '}
              <code>recoveryWrapped</code>). Wer die Phrase kennt, kann den Tresor öffnen, genauso wie mit dem
              Passwort, aber daraus lässt sich Ihr Passwort nicht rekonstruieren.
            </p>
            <Callout type="info" title="Warum das Zero-Knowledge nicht schwächt">
              Zwei Zugangswege bedeuten nicht, dass der Server einen „Hintertür“-Weg hat. Er besitzt nur die
              verschlüsselten Hüllen. Ohne <em>Ihr</em> Passwort <em>oder</em> <em>Ihre</em> Phrase kann er den
              Master-Schlüssel nicht freilegen, egal über welchen Kanal Sie normalerweise entsperren.
            </Callout>

            <h3 className="docs-h3">Passwort zurücksetzen (mit Recovery-Phrase)</h3>
            <p>
              Wenn Sie Ihr Passwort vergessen haben, aber Ihre 12 Wörter noch haben, können Sie ein neues Passwort
              setzen, ohne Ihre Dateien neu zu verschlüsseln:
            </p>
            <ol className="docs-ol">
              <li>Sie geben die Recovery-Phrase lokal ein (sie verlässt den Browser nicht).</li>
              <li>Der Browser entschlüsselt <code>recoveryWrapped</code> → Sie erhalten wieder den <strong>gleichen</strong> Master-Schlüssel.</li>
              <li>Sie wählen ein neues Passwort; daraus wird ein neuer KEK abgeleitet.</li>
              <li>Derselbe Master-Schlüssel wird unter dem neuen Passwort neu verpackt. Zusätzlich unterschreibt der Browser eine vom Server ausgegebene Einmal-Aufgabe mit dem Ed25519-Schlüssel aus Ihren 12 Wörtern. <strong>Unterschrieben wird dabei auch die neue Hülle</strong>, eine abgefangene Unterschrift lässt sich also nicht mit anderem Material weiterverwenden.</li>
              <li>Neues <code>passwordWrapped</code> plus Challenge-Proof gehen an <code>POST /api/tresor/reset-password</code>.</li>
            </ol>
            <Callout type="note" title="Was sich dabei nicht ändert">
              Der Master-Schlüssel bleibt identisch. Dateiinhalte, verschlüsseltes Journal und{' '}
              <code>recoveryWrapped</code> bleiben gültig, nur die Passwort-Hülle wird ausgetauscht.
            </Callout>
          </section>

          <section id="session" className="docs-section">
            <h2 className="docs-h2"><Lock className="docs-h2-icon" /> Entsperren, Sperren, Session</h2>
            <p>
              Nach dem Entsperren liegt Ihr Master-Schlüssel nur im <strong>Arbeitsspeicher</strong> der
              Browser-Sitzung (<code>sessionKey</code>). Er wird weder in <code>localStorage</code> noch in
              IndexedDB abgelegt. Vorher lag er ausschließlich als verschlüsselte Hülle auf dem Server; freigegeben
              wird er erst durch Ihre lokale Passwort- oder Recovery-Eingabe (siehe Abschnitt{' '}
              <a href="#passwort-recovery" className="ed-link" onClick={(e) => { e.preventDefault(); navTo('passwort-recovery'); }}>Passwort & Recovery</a>).
            </p>
            <p>
              Wenn Sie <strong>„Tresor sperren“</strong> wählen, wird der Schlüssel aus dem RAM entfernt und
              die Dateiliste ausgeblendet. Ihr Datenputzer-Login bleibt bestehen, Sie müssen sich nicht
              erneut bei datenputzer.de anmelden.
            </p>
            <Callout type="note" title="Laufende Uploads beim Sperren">
              In den Einstellungen können Sie wählen: Uploads im Hintergrund beenden lassen (Standard) oder
              streng sofort stoppen. Passwort- und Recovery-Eingaben werden nach dem Entsperren aus dem
              Formular-State entfernt.
            </Callout>
            <p>
              Zwei Ebenen sind zu unterscheiden:
            </p>
            <ul className="docs-list">
              <li><strong>Datenputzer-Login</strong> (HttpOnly-Cookie): Zugang zum Konto und Tarif</li>
              <li><strong>Tresor entsperrt</strong> (Schlüssel im RAM): Zugriff auf verschlüsselte Inhalte</li>
            </ul>
            <Callout type="note" title="Drei Wege hinein">
              Passwort, die 12 Wörter und, wenn Sie einen eingerichtet haben, ein <strong>Passkey</strong>
              (Fingerabdruck, Gesichtserkennung, Windows Hello, Sicherheitsschlüssel). Jeder Weg öffnet eine
              eigene Hülle desselben Hauptschlüssels. Der Passkey nutzt die WebAuthn-Erweiterung PRF: Das Gerät
              erzeugt aus Passkey und einem Salz 32 geheime Bytes, die es nie verlassen. Daraus wird per HKDF der
              Schlüssel für die Passkey-Hülle (<code>passkeyKekFromPrf</code>). Auf dem Server liegen nur
              Credential-ID, Salz und Hülle. Einrichten verlangt die Bestätigung mit Ihrem Passwort, eine
              gestohlene Anmeldung kann also keinen eigenen Passkey eintragen; Entfernen braucht den
              Löschnachweis. Nicht jeder Browser und nicht jedes Gerät kann PRF, dann bietet der Tresor den
              Passkey gar nicht erst an.
            </Callout>
          </section>

          <section id="ram-sicherheit" className="docs-section">
            <h2 className="docs-h2">Der Schlüssel im Arbeitsspeicher</h2>
            <p>
              Verschlüsselung nützt wenig, wenn der Schlüssel danach stundenlang im Arbeitsspeicher liegt.
              Die entscheidende Frage ist deshalb nicht nur <em>ob</em> entschlüsselt wird, sondern{' '}
              <strong>wann und wie lange</strong>. Was der Tresor dazu tut:
            </p>
            <div className="docs-table-wrap">
              <table className="ed-table docs-table">
                <thead>
                  <tr>
                    <th>Frage</th>
                    <th>Im Tresor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Wann wird der Master-Schlüssel freigelegt?</td>
                    <td>Erst wenn Sie Ihr Passwort oder Ihre 12 Wörter eingeben. Ohne Ihre Eingabe passiert nichts, auch nicht beim Öffnen der Seite.</td>
                  </tr>
                  <tr>
                    <td>Wie lange liegt er im Arbeitsspeicher?</td>
                    <td>Bis Sie sperren, die Auto-Sperre greift oder die Tab-Sperre auslöst. Danach wird er verworfen.</td>
                  </tr>
                  <tr>
                    <td>Landet er auf der Festplatte?</td>
                    <td>Nein. Auf dem Server liegen nur die Hüllen (<code>passwordWrapped</code>, <code>recoveryWrapped</code> und je Passkey eine weitere), lokal nichts. Weder <code>localStorage</code> noch IndexedDB bekommen ihn zu sehen.</td>
                  </tr>
                  <tr>
                    <td>Lässt sich das an Hardware binden?</td>
                    <td>Teilweise: Mit einem Passkey hängt das Entsperren am Gerät (Secure Enclave, TPM, Sicherheitsschlüssel). Der entsperrte Schlüssel selbst liegt danach wie immer im Arbeitsspeicher des Browsers.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="docs-muted">
              Dass diese Fragen nicht theoretisch sind, zeigte im Mai 2026 ein Fall bei Microsoft Edge:
              der Browser entschlüsselte gespeicherte Passwörter schon beim Start und hielt sie danach
              dauerhaft im Arbeitsspeicher, ohne dass jemand danach gefragt hatte.
            </p>
            <Callout type="info" title="Crypto-Worker">
              Große Chunks werden in dedizierten Web Workern verarbeitet (<code>cryptoWorker.js</code> über
              <code>cryptoClient.js</code>), je nach Gerät bis zu vier parallel. Der UI-Hauptthread hält damit
              keinen Klartext großer Dateien, nur der Worker sieht den entschlüsselten Chunk kurz während der Operation.
            </Callout>
            <Callout type="info" title="Wogegen das nicht hilft">
              Solange der Tresor entsperrt ist, steht der Schlüssel im Arbeitsspeicher. Schadsoftware mit
              Zugriff auf denselben Rechner kann ihn dort auslesen, und das kann kein Browser verhindern.
              Was sich beeinflussen lässt, ist die Dauer: sperren, wenn Sie fertig sind, die Auto-Sperre
              nutzen, und auf einem befallenen Gerät den Tresor gar nicht erst öffnen.
            </Callout>
          </section>

          <section id="threat-model" className="docs-section">
            <h2 className="docs-h2">Wogegen der Tresor schützt und wogegen nicht</h2>
            <p>
              Eine Tabelle, in der überall „geschützt" steht, ist keine Sicherheitsdokumentation,
              sondern Werbung. In zwei Zeilen steht deshalb, dass es keinen technischen Schutz gibt.
            </p>
            <div className="docs-table-wrap">
              <table className="ed-table docs-table">
                <thead>
                  <tr>
                    <th>Bedrohung</th>
                    <th>Schutz im Tresor</th>
                    <th>Ihre Maßnahme</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Server liest Dateiinhalte</td>
                    <td>Es liegt nur Kryptotext dort, der Schlüssel entsteht bei Ihnen</td>
                    <td>Ein langes, ungewöhnliches Passwort wählen: gegen die Hülle ist Raten offline möglich</td>
                  </tr>
                  <tr>
                    <td><strong>Wir liefern manipuliertes JavaScript aus</strong></td>
                    <td><strong>Technisch keiner.</strong> Wer den Code ausliefert, der verschlüsselt, könnte ihn ändern. Das gilt für jeden Anbieter, dessen Krypto im Browser läuft</td>
                    <td>Quelltext und Prüfsummen vergleichen (<a href="/nachpruefen/" className="ed-link">nachprüfen</a>). Die Prüfwerte jedes ausgelieferten Bundles stehen offen unter <code>/pruefwerte.json</code>, zu den meisten Ständen liegt zusätzlich eine Kopie im Internet Archive, also außerhalb unserer Reichweite</td>
                  </tr>
                  <tr>
                    <td>Abhörung im Netz</td>
                    <td>TLS, und die Blöcke sind schon vorher verschlüsselt</td>
                    <td>Nichts nötig</td>
                  </tr>
                  <tr>
                    <td>Schadsoftware auf Ihrem Rechner oder eine bösartige Browser-Erweiterung</td>
                    <td><strong>Keiner, solange der Tresor offen ist.</strong> Wer Ihren Rechner oder Ihren Browser kontrolliert, sieht, was Sie tippen und was der Tresor anzeigt. Das gilt für jede Anwendung, nicht nur für uns. Was wir tun: der Schlüssel ist nicht auslesbar und liegt nur im Arbeitsspeicher, Dateinamen liegen auf dem Gerät verschlüsselt, Kopieren aus Passwortfeldern ist gesperrt, der Tresor sperrt sich nach Inaktivität selbst</td>
                    <td>Sperren, wenn Sie fertig sind. Nur Erweiterungen aus vertrauenswürdiger Quelle, möglichst wenige. Auf einem befallenen Gerät den Tresor gar nicht erst öffnen</td>
                  </tr>
                  <tr>
                    <td>Fremder Code im Tresor-Fenster (XSS)</td>
                    <td>Teilweise. Die CSP erlaubt nur eigene Skripte, keine Inline-Skripte und keine Inline-Styles; mit <strong>Trusted Types</strong> nimmt der Browser zusätzlich nur Skriptadressen vom eigenen Ursprung an. Der Schlüssel ist <code>extractable: false</code>, lässt sich also nicht auslesen und mitnehmen. <strong>Benutzen kann fremder Code ihn trotzdem, solange der Tresor offen ist</strong></td>
                    <td>Sperren, wenn Sie fertig sind. Kurze Auto-Sperre einstellen</td>
                  </tr>
                  <tr>
                    <td>Jemand setzt sich an Ihr offenes Gerät</td>
                    <td>Sperre und optionale Tab-Sperre</td>
                    <td>Kurze Auto-Sperre, Gerät beim Weggehen verriegeln</td>
                  </tr>
                  <tr>
                    <td>Sie verlieren Passwort und die 12 Wörter</td>
                    <td>Keiner. Wir können den Tresor nicht öffnen</td>
                    <td>Die 12 Wörter auf Papier, an einem zweiten Ort, und jemandem sagen, dass es sie gibt</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="dashboard-einbettung" className="docs-section">
            <h2 className="docs-h2">Der Tresor im Datenputzer-Dashboard</h2>
            <p>
              Im Dashboard erscheint der Tresor als eigener Bereich. Technisch läuft er dort weiter auf seiner
              eigenen Adresse <code>tresor.datenputzer.de</code>, eingebettet als Rahmen. Das ist Absicht:
              Das Dashboard lädt Dienste wie die Zahlungsabwicklung und den Google-Login. Durch die Trennung
              kann kein Skript des Dashboards in den Tresor hineingreifen, Ihren Schlüssel benutzen oder
              Dateinamen lesen.
            </p>
            <ul className="docs-list">
              <li><strong>Was zwischen Dashboard und Tresor ausgetauscht wird:</strong> nur Bedienung. Der Tresor
              meldet „gesperrt oder offen“, belegten Platz als Zahl und seine Höhe; das Dashboard schickt das
              Farbschema und darf den Tresor sperren, etwa beim Abmelden. Beide Seiten prüfen den Absender exakt.</li>
              <li><strong>Was nie ausgetauscht wird:</strong> Schlüssel, Passwort, Datei- und Ordnernamen, Inhalte,
              unterschriebene Adressen. Das Dashboard kann im Tresor nichts auslösen außer Sperren.</li>
              <li><strong>Wer den Tresor einbetten darf:</strong> nur <code>datenputzer.de</code>, und nur über die
              eigens dafür gebaute Adresse <code>/einbettung</code>. Alle anderen Tresor-Seiten lassen sich nicht einbetten.</li>
              <li>Der Tresor bleibt auch direkt unter <code>tresor.datenputzer.de</code> erreichbar, mit demselben
              Konto und denselben Dateien.</li>
            </ul>
          </section>

          <section id="abo-ende" className="docs-section">
            <h2 className="docs-h2">Nach dem Abo-Ende und bei zu viel belegtem Speicher</h2>
            <ul className="docs-list">
              <li><strong>Abo beendet:</strong> Ihr Tresor wird nicht sofort gelöscht. Sie können Ihre Dateien
              weiter entsperren, ansehen und herunterladen, aber nichts Neues hochladen. Für das Herunterladen gilt
              ein Kontingent, das für einen vollständigen Export reicht. Nach einer Frist, die in den AGB steht,
              werden die Dateien gelöscht. Mit einem neuen Abo bleibt alles, wie es ist.</li>
              <li><strong>Mehr belegt als gebucht</strong> (zum Beispiel nach dem Kündigen von Zusatzspeicher):
              Neue Uploads sind gesperrt, bis Sie Dateien löschen oder Speicher erweitern. Bleibt die Überbelegung
              bis zum Ende der Frist bestehen, entfernen wir nur den Überschuss, beginnend mit den zuletzt
              hochgeladenen Dateien. Der gebuchte Teil bleibt unangetastet.</li>
              <li><strong>Wie wir Sie informieren:</strong> im Tresor und im Dashboard, dazu wenige E-Mails, die Sie
              abbestellen können. Die E-Mails nennen Anzahl und Größe der Dateien, keine Namen: die kennt nur Ihr
              Browser. Im Tresor sehen Sie, welche Dateien betroffen sind.</li>
            </ul>
          </section>

          <section id="sicherheits-einstellungen" className="docs-section">
            <h2 className="docs-h2">Einstellungen für mehr Sicherheit</h2>
            <p>Unter <strong>Einstellungen → Sicherheit</strong> im Tresor können Sie Folgendes konfigurieren:</p>
            <ul className="docs-list">
              <li>
                <strong>Automatisch sperren nach …</strong> (5 / 15 / 30 / 60 Minuten oder Aus): Bei Inaktivität
                wird der Master-Schlüssel aus dem RAM entfernt. Upload-Aktivität zählt als Nutzung.
              </li>
              <li>
                <strong>Sperren während Uploads:</strong> „Laufende Uploads beenden“ hält den Schlüssel kurz
                für den aktiven Job; „Sofort stoppen (streng)“ löscht ihn sofort und pausiert die Queue.
              </li>
              <li>
                <strong>Sperren wenn Tab im Hintergrund:</strong> Optional, standardmäßig aus. Sperrt beim
                Tab-Wechsel (Paranoia-Modus).
              </li>
              <li>
                <strong>Passkey:</strong> schützt vor Mitlesen beim Tippen und vor gefälschten Seiten (er funktioniert nur auf tresor.datenputzer.de), und seine Hülle lässt sich nicht erraten. Er schützt nicht vor Schadsoftware, die den bereits geöffneten Tresor beobachtet.
              </li>
            </ul>
            <CodeBlock title="src/securityPrefs.js · localStorage-Schlüssel">
{`tresor_auto_lock_minutes      // 0 | 5 | 15 | 30 | 60
tresor_lock_upload_policy      // strict | finish_current
tresor_lock_on_tab_hidden      // 0 | 1`}
            </CodeBlock>
          </section>

          <section id="dateiverschluesselung" className="docs-section">
            <h2 className="docs-h2">Dateiverschlüsselung (AES-GCM)</h2>
            <p>
              Ihre Dateien werden in <strong>5-MiB-Chunks</strong> geteilt. Jeder Chunk erhält eine frische
              12-Byte-IV. Zusätzliche authentifizierte Daten (AAD) binden den Ciphertext an eine Datei-UUID
              und den Chunk-Index, damit Stücke nicht vertauscht werden können.
            </p>
            <p>
              Seit Oktober 2026 hat <strong>jede neue Datei einen eigenen Schlüssel</strong>: 256 Bit aus dem
              Zufallsgenerator, nur für diese eine Datei. Der Master-Schlüssel verschlüsselt nicht mehr die
              Datei selbst, sondern nur diesen Dateischlüssel (<code>createFileKey</code>). Die Hülle ist per
              AAD an die Datei-UUID gebunden und lässt sich nicht auf eine andere Datei umhängen. Sie reist
              verschlüsselt im Änderungsjournal mit, so kennt jedes Ihrer Geräte sie. Ältere Dateien bleiben
              unverändert lesbar, bei ihnen ist der Master-Schlüssel weiter direkt der Dateischlüssel.
            </p>
            <CodeBlock title="src/crypto.js · Schlüssel je Datei (Auszug)">
{`export async function createFileKey(masterKey, encryptionId) {
    const bits = globalThis.crypto.getRandomValues(new Uint8Array(32));
    try {
        const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
        const ct = await globalThis.crypto.subtle.encrypt(
            { name: "AES-GCM", iv, additionalData: dekAad(encryptionId) }, masterKey, bits
        );
        …
        return { fileKey, wrappedFileKey: bytesToB64(combined) };
    } finally {
        bits.fill(0);
    }
}`}
            </CodeBlock>
            <CodeBlock title="src/crypto.js · Ein Chunk verschlüsseln">
{`export async function encryptChunk(arrayBuffer, key, fileId, chunkIndex) {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const aad = new TextEncoder().encode(\`\${fileId}:\${chunkIndex}\`);

  const encryptedContent = await globalThis.crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: aad },
    key,
    arrayBuffer
  );
  const combined = new Uint8Array(iv.length + encryptedContent.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encryptedContent), iv.length);
  return combined;
}`}
            </CodeBlock>
          </section>

          <section id="download" className="docs-section">
            <h2 className="docs-h2"><Download className="docs-h2-icon" /> Download und Entschlüsselung</h2>
            <p>
              Klicken Sie eine Datei an, holt der Browser kurzlebige, unterschriebene Adressen von unserem
              Server, mehrere Blöcke mit einer Anfrage, und lädt die Blöcke damit <strong>direkt vom
              Objektspeicher</strong>, bis zu vier gleichzeitig. Unser Server sieht den Kryptotext dabei nicht,
              er stellt nur die Adressen aus. Entschlüsselt wird anschließend lokal mit dem Schlüssel der Datei.
            </p>
            <p>
              Dateien ab 256 MB laufen <strong>im Strom</strong> auf die Festplatte: Ihr Browser entschlüsselt
              Block für Block und reicht jeden Block an einen kleinen Download-Helfer
              (<code>/herunterladen/sw.js</code>, ein Service Worker auf derselben Adresse). Der gibt die Blöcke als
              normalen Download weiter. So liegt nie die ganze Datei im Arbeitsspeicher, und auch sehr große
              Dateien lassen sich im Dashboard herunterladen. Der Helfer kennt keinen Schlüssel, macht keine
              Netzwerkanfragen und speichert nichts. In Safari, wo das nicht zuverlässig klappt, bleibt der
              bisherige Weg.
            </p>
            <CodeBlock title="src/crypto.js · Streaming-Entschlüsselung (Auszug)">
{`export async function decryptBlobStream(encryptedBlob, key, originalType, fileId) {
  const combinedArray = new Uint8Array(await encryptedBlob.arrayBuffer());
  const stream = new ReadableStream({
    async pull(controller) {
      // Chunkweise decryptChunk -> enqueue -> naechster Chunk.
      // Kein Array, das die ganze Datei im Klartext sammelt.
    }
  });
  // new Blob([stream]) waere falsch: ein ReadableStream ist kein gueltiger Blob-Teil
  // und wuerde zu "[object ReadableStream]" verstringlicht, also 23 Byte Schrott.
  // Response liest den Strom korrekt aus.
  const streamed = await new Response(stream).blob();
  return originalType ? new Blob([streamed], { type: originalType }) : streamed;
}`}
            </CodeBlock>
            <Callout type="note" title="Crypto Worker">
              Verschlüsselung und Entschlüsselung laufen in dedizierten Web Workern (<code>cryptoWorker.js</code>),
              getrennt vom UI-Hauptthread. Der Schlüssel bleibt nicht exportierbar und wird per
              Structured Clone an den Worker übergeben.
            </Callout>
            <p>
              Scheitert die Entschlüsselung (falsches Passwort, beschädigte Daten, manipulierter Chunk),
              bricht der Vorgang mit einer Fehlermeldung ab.
            </p>
          </section>

          <section id="lokaler-index" className="docs-section">
            <h2 className="docs-h2"><Database className="docs-h2-icon" /> Dateiindex: lokal und in der Cloud</h2>
            <p>
              Ihr Dateiindex (Ordner, Dateinamen, Struktur) existiert auf <strong>zwei Ebenen</strong>:
              schnell lokal in IndexedDB und verschlüsselt synchronisiert auf dem Server im{' '}
              <strong>Änderungsjournal</strong>.
            </p>

            <h3 className="docs-h3">Lokal: IndexedDB (Dexie)</h3>
            <p>
              Nach dem Entsperren liegen Ordner und Dateimetadaten in <strong>Dexie/IndexedDB</strong> unter
              <code> TresorDatabase</code> auf Ihrem Gerät. So können Sie sortieren, suchen und Dateien
              auch offline öffnen, ohne bei jedem Klick den Server zu fragen.
            </p>
            <Callout type="info" title="Der lokale Index liegt verschlüsselt">
              Datei- und Ordnernamen, Dateitypen und die Namen laufender Uploads stehen in IndexedDB nur
              als Chiffretext im Feld <code>sealed</code>. Der Schlüssel dafür (<code>indexKey</code>) wird
              beim Entsperren aus dem Master-Schlüssel abgeleitet und existiert nur im Arbeitsspeicher. Nach
              dem Sperren oder Schließen des Browsers kann dieses Gerät die Namen ohne Ihr Passwort nicht
              mehr lesen. Lesbar bleiben IDs, Dateigrößen, Zeitstempel und die Ordnerzuordnung. Bis
              Oktober 2026 lagen die Namen hier im Klartext; ältere Einträge werden beim nächsten Entsperren
              verschlüsselt. Das ist Verschlüsselung, keine forensische Löschung: was der Browser früher auf
              die Platte geschrieben hat, kann dort noch in freien Bereichen stehen.
            </Callout>
            <CodeBlock title="src/db.js · Schema (v5, Auszug)">
{`db.version(5).stores({
  folders: '++id, clientId, parentClientId, parentId, name, createdAt',
  files: '++id, clientId, folderClientId, folderId, name, encryptionId, uploadId, ...',
  fileBlobs: 'id',
  pendingUploads: 'id, fileName, uploadId, uploadedChunks, totalChunks',
  meta: 'key'  // journalLastEventId, journalDeviceTag,
               // journalDeviceSeq, journalBurnedSeq
});`}
            </CodeBlock>
            <ul className="docs-list">
              <li><code>files</code>: <code>sealed</code> (Name, Typ, Größenangabe verschlüsselt), Ordner, Größe in Byte, <code>encryptionId</code> (UUID für AAD), <code>wrappedFileKey</code>, <code>uploadId</code> für Cloud-Chunks</li>
              <li><code>fileBlobs</code>: bereits heruntergeladene Dateien, nur als Chiffretext</li>
              <li><code>pendingFileBlobs</code>: seit Oktober 2026 leer und ungenutzt. Bis dahin lag hier für die Fortsetzung nach dem Neuladen eine <strong>unverschlüsselte</strong> Kopie der Datei, das ist abgeschafft.</li>
              <li>Maximal <strong>64 Ordner</strong> pro Tresor (<code>MAX_VAULT_FOLDERS</code>)</li>
            </ul>

            <h3 className="docs-h3">Cloud: verschlüsseltes Journal</h3>
            <p>
              Für Geräteübergreifenden Sync schreibt der Browser jede Änderung (Ordner anlegen, Datei hinzufügen,
              umbenennen, verschieben, löschen) als Event ins Journal. Das Event wird mit dem Master-Schlüssel
              per AES-GCM verschlüsselt und als <code>encrypted_payload</code> an{' '}
              <code>POST /api/tresor/journal</code> gesendet. Auf dem Server liegt nur der Ciphertext in{' '}
              Änderungsjournal, keine Klartext-Dateinamen.
            </p>
            <ol className="docs-ol">
              <li>Beim Entsperren lädt <code>syncJournalFromServer()</code> neue Events (<code>GET /api/tresor/journal?since=…</code>).</li>
              <li>Jedes Event wird im Browser mit dem Master-Schlüssel entschlüsselt.</li>
              <li>Die Klartext-Metadaten werden in IndexedDB übernommen (<code>applyJournalEvent</code>).</li>
            </ol>
            <CodeBlock title="src/journal.js · Event verschlüsseln und syncen (Auszug)">
{`const encrypted_payload = await encryptJournalPayload(masterKey, {
  type: 'file_add', name: '…', parentClientId: '…', uploadId: '…', …
});
// → POST /api/tresor/journal

const event = await decryptJournalPayload(masterKey, row.encrypted_payload);
await applyJournalEvent(event);  // → IndexedDB files/folders`}
            </CodeBlock>
            <h3 className="docs-h3">Warum Verschlüsseln allein hier nicht reicht</h3>
            <p>
              Jeder Eintrag ist für sich verschlüsselt und mit einem Prüfwert versehen, aber das bindet nur
              seinen <em>Inhalt</em>, nicht seine <em>Position</em>. Ein Server könnte einzelne Einträge
              weglassen oder umsortieren: eine Datei fehlte dann still in der Liste, oder eine alte
              Umbenennung gewänne gegen eine neue. Lesen könnte er trotzdem nichts, aber ein Safe, dessen
              Inhaltsverzeichnis sich von außen kürzen lässt, ist kein guter Safe.
            </p>
            <p>
              Deshalb trägt jeder Eintrag <strong>im verschlüsselten Teil</strong> eine Gerätekennung und
              einen je Gerät fortlaufenden Zähler. Beides steht damit unter demselben Prüfwert wie der
              Inhalt und lässt sich nicht fälschen. Beim Abgleich müssen die Zähler eines Geräts eine
              lückenlose Reihe ab 1 ergeben. Fehlt eine Zahl, sagt der Tresor das.
            </p>
            <ul className="docs-list">
              <li><strong>Je Gerät, nicht global:</strong> ein globaler Zähler würde kollidieren, wenn zwei
              Ihrer Geräte gleichzeitig schreiben. Ein echter Konflikt sähe dann aus wie ein Angriff.</li>
              <li><strong>Der Tresor sperrt bei einem Fund nicht.</strong> Ihre Dateien sind unversehrt,
              nur die Liste ist womöglich unvollständig. Wer an dieser Stelle ausgesperrt würde, käme an
              gar nichts mehr heran, obwohl alles noch da ist.</li>
              <li><strong>Was das nicht erkennt:</strong> was ein Gerät nie gesehen hat, kann es nicht
              vermissen. Unterschlägt der Server die jüngsten Einträge eines <em>anderen</em> Geräts, fällt
              das hier nicht auf. Die eigenen fehlenden Einträge erkennt jedes Gerät.</li>
              <li>Einträge aus der Zeit vor dieser Prüfung tragen keinen Zähler und werden davon
              ausgenommen, sonst schlüge jeder bestehende Tresor sofort Alarm.</li>
            </ul>
            <Callout type="info" title="Zusammenfassung">
              <strong>Server:</strong> verschlüsselte Journal-Blobs + Upload-Metadaten (Quota, Chunk-Anzahl).{' '}
              <strong>Browser nach Entsperren:</strong> lesbarer Dateiindex in IndexedDB. Ohne Master-Schlüssel
              kann der Betreiber weder Dateinamen noch Ordnerstruktur aus dem Journal lesen.
            </Callout>
          </section>

          <section id="upload" className="docs-section">
            <h2 className="docs-h2"><UploadCloud className="docs-h2-icon" /> Upload</h2>
            <ol className="docs-ol">
              <li>Sie wählen oder ziehen eine Datei in den Tresor.</li>
              <li>Der Browser teilt sie in 5-MiB-Stücke und verschlüsselt jedes Stück.</li>
              <li>Sie sehen Fortschritt: <em>Vorbereiten → Verschlüsseln → Übertragen → Abschließen</em></li>
              <li>Für die Stücke holt der Browser kurzlebige, unterschriebene Adressen, mehrere mit einer
              Anfrage, und lädt den Kryptotext per <code>PUT</code>{' '}
              <strong>direkt zum Objektspeicher</strong>. Wie viele Stücke gleichzeitig unterwegs sind, richtet
              sich nach Ihrer Leitung: drei zu Beginn, bis zu sechs, solange es schneller wird. Unser Server
              bekommt die Bytes nie zu sehen, er stellt nur die Adressen aus und zählt den belegten Platz. Nach{' '}
              <code>POST /api/tresor/upload/complete</code> ist die Datei gesichert.</li>
            </ol>
            <CodeBlock title="src/uploads.js · Chunk verschlüsseln und übertragen (Auszug)">
{`export const CHUNK_SIZE = 5 * 1024 * 1024;

const chunkBuffer = await file.slice(sliceStart, sliceEnd).arrayBuffer();
encryptedChunk = await encryptChunk(chunkBuffer, chunkKey, fileUuid, c);

// Kurzlebige unterschriebene Adresse von unserem Server holen ...
const { url } = await (await authenticatedFetch(\`\${API_URL}/upload/sign\`, {
  method: 'POST', body: JSON.stringify({ uploadId, chunkIndex: c })
})).json();
// ... und den Kryptotext direkt zum Objektspeicher schieben.
// Bewusst ohne Authorization-Kopf: die Adresse IST der Ausweis.
await fetch(url, { method: 'PUT', body: chunkBody });`}
            </CodeBlock>
            <Callout type="note" title="Upload fortsetzen">
              Bricht die Verbindung ab oder sperren Sie den Tresor streng, läuft der Upload beim nächsten
              Entsperren weiter, solange der Tab offen bleibt. Nach dem Neuladen geht das nicht mehr: Dafür
              müsste eine Kopie der Datei im Browser liegen, und genau die soll es nicht geben. Die
              Fortschrittsdaten in <code>pendingUploads</code> sind verschlüsselt wie der übrige Index.
            </Callout>
          </section>

          <section id="loeschnachweis" className="docs-section">
            <h2 className="docs-h2">Löschen nur mit offenem Tresor</h2>
            <p>
              Wer Ihre Anmeldung stiehlt, etwa über Ihr Postfach, kann Ihre Dateien nicht lesen. Ohne
              weiteren Schutz könnte er sie aber in den Papierkorb werfen, den der Server nach 30 Tagen leert,
              oder sofort endgültig löschen. Deshalb verlangt der Server für jede Löschung eine Unterschrift.
            </p>
            <ol className="docs-ol">
              <li>Beim Einrichten leitet der Browser aus dem Master-Schlüssel ein Ed25519-Schlüsselpaar ab und
              schickt nur den <strong>öffentlichen</strong> Teil mit (<code>deleteProofKey</code>).</li>
              <li>Vor jedem Löschen holt der Browser eine einmalige Aufgabe (<code>POST /api/tresor/delete-challenge</code>,
              2 Minuten gültig) und unterschreibt sie zusammen mit der Aktion und der sortierten Liste der Upload-IDs.</li>
              <li>Der Server prüft die Unterschrift gegen den gespeicherten öffentlichen Schlüssel. Passt sie
              nicht, lehnt er ab. Das gilt für den Papierkorb, für ganze Ordner und für das endgültige Löschen.
              Wiederherstellen braucht keinen Nachweis.</li>
            </ol>
            <CodeBlock title="src/crypto.js · Was unterschrieben wird">
{`export function buildDeleteProofPayload({ userId, challenge, issuedAt, action, uploadIds }) {
    const ids = [...uploadIds].map(String).sort().join(',');
    return ['v1', 'tresor-delete', String(userId), challenge, String(issuedAt), action, ids].join(':');
}`}
            </CodeBlock>
            <Callout type="warn" title="Wogegen das nicht hilft">
              Gegen uns als Betreiber schützt die Unterschrift nicht: wer den Server kontrolliert, kann die
              Prüfung abschalten. Dass der Speicher gelöschte Blöcke 30 Tage als ältere Fassung aufbewahrt,
              hilft gegen Löschen über die App, etwa mit einer gestohlenen Anmeldung. Wer den Zugang zum
              Speicher selbst hat, kann auch die älteren Fassungen entfernen. Davor schützt erst eine Kopie
              mit getrenntem Zugang an einem zweiten Ort, und die gibt es noch nicht. Lesen kann die Blöcke
              in keinem Fall jemand.
            </Callout>
          </section>

          <section id="metadaten" className="docs-section">
            <h2 className="docs-h2"><Eye className="docs-h2-icon" /> Welche Metadaten sichtbar sind</h2>
            <p>
              Zero-Knowledge schützt <strong>Inhalte und Schlüssel</strong>, nicht automatisch alle Metadaten.
              Das sollten Sie bei Ihrer Risikoabwägung kennen:
            </p>
            <div className="docs-table-wrap">
              <table className="ed-table docs-table">
                <thead>
                  <tr>
                    <th>Information</th>
                    <th>Verschlüsselt?</th>
                    <th>Wo sichtbar?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Dateiinhalt (PDF, Bild, …)</td><td>Ja</td><td>Nur bei Ihnen nach Entschlüsselung</td></tr>
                  <tr><td>Master-Schlüssel, Passwort, Recovery</td><td>N/A</td><td>Nur kurz im RAM nach Entsperren</td></tr>
                  <tr><td>Dateiname, Ordnername</td><td>Ja (im Journal)</td><td>Verschlüsselt im Änderungsjournal auf dem Server; Klartext nur lokal nach Entsperren</td></tr>
                  <tr><td>Dateigröße, Chunk-Anzahl, Zeitstempel</td><td>Nein</td><td>Server (pro Datei, unverschlüsselt)</td></tr>
                  <tr><td>Tarif, Speicherbelegung</td><td>Nein</td><td>Server (für Abrechnung)</td></tr>
                  <tr><td>Login-Session</td><td>Nein</td><td>HttpOnly-Cookie Ihres Browsers</td></tr>
                  <tr><td>Verschlüsselte Blöcke selbst</td><td>Ja</td><td>Objektspeicher bei Hetzner in Nürnberg. Der Anbieter sieht Größe, Anzahl und Zugriffszeiten der Blöcke, aber keinen Inhalt und keine Dateinamen</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="krypto-parameter" className="docs-section">
            <h2 className="docs-h2">Kryptografische Parameter (Referenz)</h2>
            <ParamTable rows={[
              ['Passwort-Ableitung', 'PBKDF2-SHA256, 600.000 Iterationen'],
              ['Verschlüsselung', 'AES-256-GCM'],
              ['IV pro Chunk', '12 Byte, zufällig (Web Crypto)'],
              ['AAD-Bindung', 'fileUuid + Chunk-Index'],
              ['Chunk-Größe (Klartext)', '5 MiB'],
              ['Recovery-Phrase', 'BIP39, 12 Wörter (128 Bit + 4 Bit Prüfsumme)'],
              ['Reset-Nachweis', 'Ed25519, Server hält nur den öffentlichen Schlüssel'],
              ['Einmal-Aufgabe beim Reset', '32 Byte zufällig, 5 Minuten gültig, einmalig verwendbar'],
              ['Max. Ordner', '64 pro Tresor'],
              ['Session-Schlüssel', 'Nicht exportierbar, nur RAM'],
            ]} />
          </section>

          <section id="kdf-wahl" className="docs-section">
            <h2 className="docs-h2">Warum PBKDF2 und nicht Argon2?</h2>
            <p>
              Argon2id ist heute der Goldstandard für Passwort-KDFs. Wir nutzen bewusst <strong>PBKDF2-HMAC-SHA256
              mit 600.000 Iterationen</strong> im Browser:
            </p>
            <ul className="docs-list">
              <li>Die native Web-Crypto-API (<code>crypto.subtle</code>) unterstützt kein Argon2id.</li>
              <li>Eine WASM-Library würde eine zusätzliche Supply-Chain-Angriffsfläche im Zero-Knowledge-Kontext eröffnen.</li>
              <li>600.000 Iterationen sind der aktuelle OWASP-Wert für PBKDF2-HMAC-SHA256, das Verfahren selbst ist in NIST SP 800-132 beschrieben.</li>
              <li>Was das nicht leistet: Argon2id erschwert Angriffe zusätzlich über den Speicherbedarf, PBKDF2 nur über Rechenzeit. Spezialhardware rechnet PBKDF2 daher günstiger durch. Ein langes, ungewöhnliches Passwort gleicht das aus, ein kurzes oder geläufiges nicht.</li>
            </ul>
          </section>

          <section id="server" className="docs-section">
            <h2 className="docs-h2"><Server className="docs-h2-icon" /> Server (Überblick)</h2>
            <p>
              Hinter <code>/api/tresor/*</code> läuft ein separater Dienst. Er prüft Ihre Datenputzer-Session,
              verwaltet Tarif und belegten Platz und stellt kurzlebige unterschriebene Adressen aus.
              Die verschlüsselten Blöcke selbst laufen <strong>nicht über ihn</strong>, sondern direkt zwischen
              Ihrem Browser und dem Objektspeicher.
            </p>
            <p>
              <strong>Trennung zwischen Konten:</strong> Jede Route prüft, dass die angefragte Datei zu Ihrem
              Konto gehört, bevor sie eine Adresse ausstellt. Unterschriebene Adressen gelten zwei Minuten und
              nur für einen Block unter Ihrer eigenen Kontonummer. Der Objektspeicher selbst lässt ohne Unterschrift
              weder Lesen noch Auflisten zu. Selbst wer eine fremde Upload-ID kennt, bekommt keinen Block: die
              Anfrage endet mit „nicht gefunden“.
            </p>
            <p>
              Wer prüfen will, was zwischen Browser und Server läuft, sieht jede Anfrage im Netzwerk-Tab der
              Entwicklertools, mit Inhalt. Dort lässt sich nachvollziehen, dass weder Passwort noch Schlüssel
              noch Klartext je an uns gehen.
            </p>
            <Callout type="warn" title="Was Sie hier nicht finden">
              Interne Prüfungen, die Zugangsdaten zum Objektspeicher, Webhook-Details und Betriebsanweisungen
              stehen hier nicht. Das ist der Teil, den Sie uns tatsächlich glauben müssen. Er ändert nichts
              daran, dass die Verschlüsselung in Ihrem Browser passiert: das ist der Teil, den Sie nachsehen
              können, und genau dafür liegt der Quelltext offen.
            </Callout>
          </section>

          <section id="pruefen" className="docs-section">
            <h2 className="docs-h2"><Eye className="docs-h2-icon" /> So können Sie es selbst prüfen</h2>
            <p>
              Diese Seite beschreibt, wie der Tresor gebaut ist. Ob Ihr Browser wirklich genau diesen
              Code ausführt, ist eine eigene Frage, und dafür gibt es eine eigene Seite mit einer
              Schritt-für-Schritt-Anleitung:{' '}
              <a href="/nachpruefen/" className="ed-link">Quelltext nachprüfen</a>.
            </p>
            <p>Dort steht unter anderem:</p>
            <ol className="docs-ol">
              <li>
                <strong>Der Krypto-Worker in voller Länge.</strong> Die Datei ist gut ein Kilobyte groß.
                Sie können sie ganz lesen und selbst nachsehen, dass darin keine einzige Anweisung steht,
                die etwas ins Netz schicken könnte, und kein einziges Wort über Passwörter.
              </li>
              <li>
                <strong>Haltepunkt im lesbaren Original.</strong> Wir liefern Sourcemaps mit, deshalb
                zeigen die Entwicklertools den echten Quelltext samt Kommentaren statt des gestauchten
                Codes. Sie können mitten in der Verschlüsselung anhalten und die Werte ansehen.
              </li>
              <li>
                <strong>Der Gegenbeweis.</strong> Über die Überschreibungen der Entwicklertools können Sie
                den Krypto-Code bei sich lokal kaputt machen. Geht der Upload danach nicht mehr, war es
                der echte Code.
              </li>
              <li>
                <strong>Application → IndexedDB → TresorDatabase:</strong> Unter <code>fileBlobs</code> sehen
                Sie nur verschlüsselte Daten, keine Klartext-Dateien.
              </li>
            </ol>
            <p className="docs-muted">
              Die Prüfsumme der Dateien, die Ihr Browser gerade geladen hat, rechnet er dort selbst aus.
              Verglichen wird sie gegen unser offenes Logbuch unter{' '}
              <a href="/pruefwerte.json" className="ed-link" target="_blank" rel="noreferrer">/pruefwerte.json</a>,
              in dem jeder ausgelieferte Stand steht, jeder Eintrag mit dem vorherigen verkettet. Zu den
              meisten Ständen liegt zusätzlich eine Kopie im Internet Archive. Wir könnten das Logbuch also
              nicht rückwirkend umschreiben, ohne dass die Kette bricht.
            </p>
          </section>

          <section id="grenzen" className="docs-section">
            <h2 className="docs-h2">Grenzen und Risiken</h2>

            <h3 className="docs-h3">Häufige Fragen</h3>
            <div className="ed-faq">
              <p className="ed-faq-q">Kann der Server mein Passwort aus der Recovery-Phrase ableiten?</p>
              <p>Nein. Passwort und Phrase sind getrennte Kanäle zu demselben Master-Schlüssel. Der Server hat nur verschlüsselte Hüllen, keine der beiden Geheimnisse.</p>
            </div>
            <div className="ed-faq">
              <p className="ed-faq-q">Verliere ich meine Dateien, wenn ich nur das Passwort zurücksetze?</p>
              <p>Nein, solange Sie die Recovery-Phrase haben und der Master-Schlüssel unverändert bleibt. Es wird nur die Passwort-Hülle erneuert.</p>
            </div>
            <div className="ed-faq">
              <p className="ed-faq-q">Sieht der Server meine Dateinamen?</p>
              <p>Nicht im Klartext. Sie stecken verschlüsselt im Journal. Sichtbar bleiben Speicherbelegung, Upload-IDs, Chunk-Anzahlen, Dateigrößen und Zeitstempel (siehe <a href="#metadaten" className="ed-link" onClick={(e) => { e.preventDefault(); navTo('metadaten'); }}>Metadaten</a>).</p>
            </div>

            <ul className="docs-list">
              <li>
                <strong>Ihr Gerät:</strong> Schadsoftware oder Browser-Erweiterungen könnten beim Entsperren
                theoretisch auf den Arbeitsspeicher zugreifen. Halten Sie Ihr System und Ihre Extensions sauber.
              </li>
              <li>
                <strong>Verlorenes Passwort + verlorene Recovery-Phrase:</strong> Ihre Daten sind unwiderruflich
                weg. Wir können sie nicht zurückholen, weil wir sie nie lesen konnten. Ein Anbieter, der
                Ihr Passwort zurücksetzen kann, kann auch Ihre Dokumente lesen; beides zusammen geht nicht.
              </li>
              <li>
                <strong>Fremder Code auf der Seite:</strong> Die Content-Security-Policy erlaubt nur Skripte
                von dieser Domain und keine Inline-Skripte. Im Krypto-Pfad steckt außerdem <strong>keine
                einzige fremde Bibliothek</strong>: <code>crypto.js</code> nutzt nur die eingebaute Web-Crypto
                des Browsers und die BIP39-Wortliste. Was es nicht gibt, kann auch nicht über eine
                Lieferkette vergiftet werden. Bliebe der Fall, dass <em>wir</em> manipulierten Code
                ausliefern, und genau dafür gibt es <a href="/nachpruefen/" className="ed-link">Quelltext nachprüfen</a>.
              </li>
              <li>
                <strong>Kein Teilen:</strong> Der Datenputzer Tresor ist ein persönlicher Zero-Knowledge-Speicher
                für einen Nutzer. Teilen mit anderen ist nicht eingebaut. Der Schlüssel je Datei wäre die
                Voraussetzung dafür; solange es nicht da ist, verlässt keine Datei Ihren Tresor.
              </li>
              <li>
                <strong>Verfügbarkeit ist kein Krypto-Versprechen:</strong> Die Verschlüsselung schützt
                Ihre Inhalte, nicht ihren Fortbestand. Gegen einen Ausfall auf unserer Seite hilft nur
                eine eigene Kopie. Laden Sie sich das, worauf es Ihnen wirklich ankommt, zusätzlich herunter.
              </li>
            </ul>
            <p>
              Hinweise oder Anregungen:{' '}
              <a href="mailto:support@datenputzer.de" className="ed-link">support@datenputzer.de</a>
            </p>
          </section>

          <footer className="docs-footer">
            <hr className="ed-rule" />
            <p className="ed-eyebrow">Datenputzer Tresor · Öffentliche Client-Dokumentation · Stand Oktober 2026</p>
          </footer>
        </main>
      </div>

      <SiteFooter />
    </div>
  );
}
